import {
  EventStatusEnum,
  getPredictionEventForSecurity,
  type IPredictionOutcomeEvent,
  type PredictionMarketCardConfig,
  type Security,
  type SecurityTypes,
  SecurityTypesUtils,
} from '@talos/kyoko';

export { getPredictionEventForSecurity };

/** Minimal event fields used to match outcome securities from card config before the full event loads. */
export type PredictionEventRef = {
  EventID: string;
  EventSymbol: string;
  EventName: string;
};

export type PredictionMarketSecurity = SecurityTypes['PredictionOutcome'];

export function toPredictionEventRef(
  event: Pick<IPredictionOutcomeEvent, 'EventID' | 'EventSymbol' | 'EventName'> | PredictionEventRef
): PredictionEventRef {
  return {
    EventID: String(event.EventID),
    EventSymbol: event.EventSymbol,
    EventName: event.EventName,
  };
}

export function isPredictionMarketEventTradable(event: Pick<IPredictionOutcomeEvent, 'Status'>): boolean {
  return event.Status === EventStatusEnum.Open;
}

function formatPredictionEventKey(eventKey: string): string {
  return eventKey.replace(/-/g, ' ');
}

export function getPredictionEventForCardConfig(
  config: Pick<PredictionMarketCardConfig, 'eventID' | 'eventSymbol'>,
  eventsByID: Map<string, IPredictionOutcomeEvent>
): IPredictionOutcomeEvent | undefined {
  const directMatch = eventsByID.get(config.eventID);
  if (directMatch) {
    return directMatch;
  }

  for (const event of eventsByID.values()) {
    if (event.EventSymbol === config.eventSymbol || event.EventSymbol === config.eventID) {
      return event;
    }
  }

  return undefined;
}

export function getPredictionEventListLabel(
  security: Security,
  eventsByID: Map<string, IPredictionOutcomeEvent>
): string {
  const event = getPredictionEventForSecurity(security, eventsByID);
  if (event?.EventName) {
    return event.EventName;
  }

  if (event?.EventID != null) {
    return formatPredictionEventKey(String(event.EventID));
  }

  return security.DisplaySymbol ?? security.Symbol;
}

export function getPredictionEventTitleForSecurity(
  security: Security,
  eventsByID?: Map<string, IPredictionOutcomeEvent>
): string | undefined {
  if (!SecurityTypesUtils.PredictionOutcome.isProductType(security) || !eventsByID || eventsByID.size === 0) {
    return undefined;
  }

  return getPredictionEventListLabel(security, eventsByID);
}

/** Match Outcome SearchSelect queries against Symbol when it differs from DisplaySymbol. */
export function matchesPredictionOutcomeSearch(outcome: Security, searchString: string): boolean {
  return outcome.Symbol.toLowerCase().includes(searchString);
}
